import { MenuItem } from "@chakra-ui/react";
import AddCreditFacilityEventModal, {
  AllowedCreditFacilityFundFamilyEventSubTypes,
} from "features/credit-facility/CreditFacilityAddEventModal";
import { useCreditFacilityEventDelete } from "features/credit-facility/CreditFacilityEventDelete";
import { CreditFacilityTabProps } from "features/credit-facility/tabs/CreditFacilityPageTabs";
import { useCreditFacilityEventsTableDataRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { CreditFacilityStateMachineFieldsEnum } from "gen/model/creditFacilityStateMachineFieldsEnum";
import { FundFamilyEventSubTypeEnum } from "gen/model/fundFamilyEventSubTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { DropdownButton } from "shared/components/button/DropdownButton";
import { ClickableElementLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import { RouteLink } from "shared/components/link/RouteLink";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { useTableBoxMinHeight } from "shared/components/table/box/TableBoxMinHeight";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { match } from "ts-pattern";

const PrincipalEventDropdown = () => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const [eventSubType, setEventSubType] =
    useState<AllowedCreditFacilityFundFamilyEventSubTypes | null>(null);
  const { creditFacility } = useOutletContext<CreditFacilityTabProps>();
  const isDisabled =
    !creditFacility ||
    creditFacility.state === CreditFacilityStateMachineFieldsEnum.draft;

  return (
    <>
      <Restricted permission={PermissionTypeEnum.write}>
        <DropdownButton
          title={t("creditFacility:runPrincipalTransactions")}
          size="xs"
          isDisabled={isDisabled}
        >
          <MenuItem
            as={RouteLink}
            onClick={() => setEventSubType(FundFamilyEventSubTypeEnum.drawdown)}
          >
            {t("creditFacility:drawdown.type")}
          </MenuItem>
          <MenuItem
            as={RouteLink}
            onClick={() => setEventSubType(FundFamilyEventSubTypeEnum.paydown)}
          >
            {t("creditFacility:paydown.type")}
          </MenuItem>
          <MenuItem
            as={RouteLink}
            onClick={() =>
              setEventSubType(FundFamilyEventSubTypeEnum.reallocation)
            }
          >
            {t("creditFacility:reallocation.title")}
          </MenuItem>
          <MenuItem
            as={RouteLink}
            onClick={() =>
              setEventSubType(FundFamilyEventSubTypeEnum.merge_tranches)
            }
          >
            {t("creditFacility:merge.eventTitle")}
          </MenuItem>
        </DropdownButton>
      </Restricted>
      {eventSubType && (
        <AddCreditFacilityEventModal
          eventSubType={eventSubType}
          onCloseComplete={() => setEventSubType(null)}
        />
      )}
    </>
  );
};

export const CreditFacilityEventsTable: React.FC = () => {
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "creditFacility"]);

  const hasWritePermission = useHasPermission("write");

  const { data: eventData } = useCreditFacilityEventsTableDataRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const confirmDeleteEvent = useCreditFacilityEventDelete(
    fundFamilyId,
    creditFacilityId,
  );

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type !== "credit_facility_event") {
        return undefined;
      }
      const fundFamilyEventId = metadata.event.id;
      const eventRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId })
        .event({ fundFamilyEventId });
      return match<
        FundFamilyEventSubTypeEnum,
        ClickableElementLinkInfo | undefined
      >(metadata.event.sub_type)
        .with(FundFamilyEventSubTypeEnum.drawdown, () => ({
          type: "generic",
          to: eventRoute.drawdown({}).$,
        }))
        .with(FundFamilyEventSubTypeEnum.paydown, () => ({
          type: "generic",
          to: eventRoute.paydown({}).$,
        }))
        .with(FundFamilyEventSubTypeEnum.reallocation, () => ({
          type: "generic",
          to: eventRoute.reallocation({}).$,
        }))
        .with(FundFamilyEventSubTypeEnum.merge_tranches, () => ({
          type: "generic",
          to: eventRoute
            .mergeTranches({})
            .creditFacilitiesCreditFacilityEventMergeTranche({}).$,
        }))
        .otherwise(() => undefined);
    },
    [creditFacilityId, fundFamilyId],
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (
        metadata?.type === "credit_facility_event" &&
        metadata.event.state === "draft" &&
        hasWritePermission
      ) {
        return {
          type: "menu",
          actions: (
            <MenuItem
              key="discard"
              onClick={() => {
                void confirmDeleteEvent({
                  fundFamilyEventId: metadata.event.id,
                });
              }}
              className="destructive"
            >
              {t("common:discardDraft")}
            </MenuItem>
          ),
        };
      }
      return undefined;
    },
    [confirmDeleteEvent, hasWritePermission, t],
  );

  const minHeight = useTableBoxMinHeight(eventData.rows.length, 2);

  return (
    <TableBox isFullHeight={false} expandedMinHeight={minHeight}>
      <TableBoxHeader>
        <TableBoxTitle>
          {t("creditFacility:summary.events.title")}
        </TableBoxTitle>
        {eventData.rows.length ? (
          <DataTableBoxActions>
            <PrincipalEventDropdown />
          </DataTableBoxActions>
        ) : (
          <TableBoxActions>
            <PrincipalEventDropdown />
          </TableBoxActions>
        )}
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={eventData}
          getLinkInfoForRow={getLinkInfoForRow}
          renderRowActions={renderRowActions}
        />
      </TableBoxBody>
    </TableBox>
  );
};
